// ─── src/CommunityCatalystsView.tsx ───────────────────────────────────────────
import { useState, useCallback } from "react";
import { Download, Users, MapPin, Search, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCommunityCatalysts,
  useCommunityCatalystStats,
  useDeleteCommunityCatalyst,
  communityCatalystKeys,
} from "./hooks/useCommunityCatalysts";
import { CommunityCatalystsTable } from "./components/dashboard/CommunityCatalystsTable";
import { Pagination }    from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard }      from "./components/ui/StatCard";
import type { CommunityCatalystRegistration } from "./types/communityCatalyst";

const LIMIT = 20;

function exportToCsv(rows: CommunityCatalystRegistration[]) {
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const header = [
    "Full Name", "Gender", "Email", "Phone", "Governorate", "City/Area",
    "Available 9-11 Jul", "Damascus Accommodation", "Room Sharing",
    "T-Shirt", "Dietary", "Health Needs", "Current Initiative",
    "Bootcamp Goal", "Selected Sessions", "Photo URL", "LinkedIn",
    "Social", "Notes", "Registered At",
  ].join(",");
  const body = rows.map((r) =>
    [
      r.fullNameAr, r.gender ?? "", r.email, r.phone, r.governorate, r.cityArea,
      r.availableJul9to11, r.hasDamascusAccommodation, r.roomSharingPreference,
      r.tShirtSize, r.dietaryPreference, r.healthNeeds, r.currentInitiative,
      r.bootcampGoal, (r.selectedSessions ?? []).join(" | "), r.photoUrl,
      r.linkedInUrl ?? "", r.socialUrl ?? "", r.notes ?? "",
      r.createdAt,
    ].map(escape).join(","),
  );
  const csv  = [header, ...body].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `community_catalysts_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CommunityCatalystsView() {
  const qc = useQueryClient();

  const [search,      setSearch]      = useState("");
  const [governorate, setGovernorate] = useState("");
  const [visualPage,  setVisualPage]  = useState(1);

  const { data: stats } = useCommunityCatalystStats();

  // All rows for CSV export (unfiltered, high limit)
  const { data: allData } = useCommunityCatalysts({ page: 1, limit: 2000 });
  const allRows = allData?.data ?? [];

  const { data: tableData, isLoading, isFetching, isError } = useCommunityCatalysts({
    page:  visualPage,
    limit: LIMIT,
    ...(search      ? { search }      : {}),
    ...(governorate ? { governorate } : {}),
  });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteCommunityCatalyst();

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  const govEntries = Object.entries(stats?.byGovernorate ?? {}).sort((a, b) => b[1] - a[1]);
  const topGovs = govEntries.slice(0, 3);

  const handleSearch = useCallback((v: string) => {
    setSearch(v);
    setVisualPage(1);
  }, []);

  const handleGovFilter = useCallback((g: string) => {
    setGovernorate(g);
    setVisualPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: communityCatalystKeys.all });
  }, [qc]);

  return (
    <div className="space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Registrations" value={stats?.total ?? 0} icon={Users} accent="bg-brand-green" />
        {topGovs.map(([name, count]) => (
          <StatCard key={name} label={name} value={count} icon={MapPin} accent="bg-brand-teal" />
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name, email or phone…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-card border border-brand-border
                       rounded-lg text-white placeholder-brand-muted
                       focus:outline-none focus:border-brand-green transition-colors"
          />
          {search && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Governorate filter */}
        <select
          value={governorate}
          onChange={(e) => handleGovFilter(e.target.value)}
          className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg
                     text-white focus:outline-none focus:border-brand-green transition-colors"
        >
          <option value="">All Governorates</option>
          {govEntries.map(([name]) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="btn-ghost text-sm flex items-center gap-1.5"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <button
            onClick={() => exportToCsv(allRows)}
            disabled={allRows.length === 0}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load registrations. Check your API key or network.
        </div>
      ) : (
        <>
          <CommunityCatalystsTable
            rows={pageRows}
            onDelete={(id) => deleteRow(id)}
            isDeleting={isDeleting}
          />
          <Pagination
            page={tableMeta.page}
            totalPages={tableMeta.totalPages}
            total={tableMeta.total}
            limit={LIMIT}
            onPageChange={setVisualPage}
            isFetching={isFetching}
          />
        </>
      )}
    </div>
  );
}
